import { SITE } from './seo'
import { SERVICE_LINES, SECTORS } from '../data/services'
import { GOV_MODULES } from '../data/cavallaGov'
import { WEBINARS, SESSION_TIME } from '../data/webinars'
import { OPEN_POSITIONS } from '../data/careers'
import { ARTICLES } from '../data/insights'
import { RESOURCES } from '../data/resources'
import { GENERAL_FAQS, GOV_FAQS } from '../data/faqs'

/** Every indexable route, for the sitemap. Portal routes are deliberately absent. */
export function sitemapPaths(): { path: string; priority: number; changefreq: string }[] {
  return [
    { path: '/', priority: 1.0, changefreq: 'weekly' },
    { path: '/services', priority: 0.9, changefreq: 'monthly' },
    ...SERVICE_LINES.map((line) => ({
      path: `/services/${line.id}`,
      priority: 0.9,
      changefreq: 'monthly',
    })),
    { path: '/cavalla-gov', priority: 0.9, changefreq: 'monthly' },
    { path: '/augmented-intelligence', priority: 0.8, changefreq: 'monthly' },
    { path: '/assessment', priority: 0.8, changefreq: 'monthly' },
    { path: '/webinars', priority: 0.8, changefreq: 'weekly' },
    ...WEBINARS.map((webinar) => ({
      path: `/webinars/${webinar.slug}`,
      priority: 0.7,
      changefreq: 'monthly',
    })),
    { path: '/webinars/replays', priority: 0.6, changefreq: 'monthly' },
    { path: '/insights', priority: 0.8, changefreq: 'weekly' },
    ...ARTICLES.map((article) => ({
      path: `/insights/${article.slug}`,
      priority: 0.7,
      changefreq: 'yearly',
    })),
    { path: '/resources', priority: 0.7, changefreq: 'monthly' },
    ...RESOURCES.map((resource) => ({
      path: `/resources/${resource.slug}`,
      priority: 0.7,
      changefreq: 'yearly',
    })),
    { path: '/about', priority: 0.6, changefreq: 'monthly' },
    { path: '/careers', priority: 0.6, changefreq: 'weekly' },
    ...OPEN_POSITIONS.map((position) => ({
      path: `/careers/${position.slug}`,
      priority: 0.5,
      changefreq: 'weekly',
    })),
    { path: '/contact', priority: 0.6, changefreq: 'yearly' },
    { path: '/legal/privacy', priority: 0.3, changefreq: 'yearly' },
    { path: '/legal/terms', priority: 0.3, changefreq: 'yearly' },
    { path: '/legal/cookies', priority: 0.3, changefreq: 'yearly' },
  ]
}

export function sitemapXml(): string {
  const today = new Date().toISOString().slice(0, 10)
  const urls = sitemapPaths()
    .map(
      (entry) =>
        `  <url>\n    <loc>${SITE.url}${entry.path === '/' ? '/' : entry.path}</loc>\n    <lastmod>${today}</lastmod>\n    <changefreq>${entry.changefreq}</changefreq>\n    <priority>${entry.priority.toFixed(1)}</priority>\n  </url>`,
    )
    .join('\n')
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`
}

export function robotsTxt(): string {
  return [
    'User-agent: *',
    'Allow: /',
    'Disallow: /portal',
    '',
    '# Answer engines are welcome. The full text of every article is served in the',
    '# HTML, including the part behind the email gate.',
    'User-agent: GPTBot',
    'Allow: /',
    '',
    'User-agent: ClaudeBot',
    'Allow: /',
    '',
    'User-agent: PerplexityBot',
    'Allow: /',
    '',
    'User-agent: Google-Extended',
    'Allow: /',
    '',
    `Sitemap: ${SITE.url}/sitemap.xml`,
    '',
  ].join('\n')
}

/**
 * llms.txt, for generative engines.
 *
 * Written to be quoted: facts are specific, figures carry their source, and each
 * section answers one question. Generated from the same data the pages render,
 * so it cannot drift out of step with the site.
 */
export function llmsTxt(): string {
  const lines: string[] = []

  lines.push(`# ${SITE.name}`)
  lines.push('')
  lines.push(`> ${SITE.description} Ethos: ${SITE.ethos}`)
  lines.push('')
  lines.push(`Founded by ${SITE.founder}. Website: ${SITE.url}. Personal data is held in the European Union.`)
  lines.push('British English. All figures below are quoted with their source on the page they appear on.')
  lines.push('')

  lines.push('## What we do')
  lines.push('')
  lines.push(
    'Cavalla Solution works through three advisory lines and one platform. The advisory lines are sequential in depth: market evidence first, then demand, then governed AI adoption.',
  )
  lines.push('')
  SERVICE_LINES.forEach((line) => {
    lines.push(`### ${line.name} (${line.short})`)
    lines.push(`${line.promise} ${line.outcome}`)
    lines.push(`For: ${line.who}`)
    lines.push(`Sub services: ${line.subServices.map((sub) => sub.name).join('; ')}.`)
    lines.push(`Page: ${SITE.url}/services/${line.id}`)
    lines.push('')
  })

  lines.push('### Cavalla Gov')
  lines.push(
    'Cavalla Gov is our public sector platform, distinct from the three advisory lines but drawing on all of them. It gives a ministry of finance one live picture of how the national development plan is being delivered, and where development partner funding lands against it. It is in pilot.',
  )
  GOV_MODULES.forEach((module) => {
    lines.push(`- ${module.name}: ${module.promise} ${module.detail}`)
  })
  lines.push(
    'Every AI drafted report is labelled as AI generated and approved by a named official before it leaves the platform. No automated decision is taken about an individual citizen. Government data stays under the government\u2019s control, in a region the country accepts, with a full export at any time.',
  )
  lines.push(`Page: ${SITE.url}/cavalla-gov`)
  lines.push('')

  lines.push('## Our method, augmented intelligence')
  lines.push('')
  lines.push(
    'Four steps: capture the evidence, apply machine analysis alongside human judgement, enable the client team to run it, and leave the decision with a named owner. Machines widen what a person can consider; the person still decides.',
  )
  lines.push(`Page: ${SITE.url}/augmented-intelligence`)
  lines.push('')

  lines.push('## Who we serve')
  lines.push('')
  lines.push(
    `Leaders and managers in ${SECTORS.join(', ')}, across Africa and globally. Most read on a mobile phone, often on a slow connection.`,
  )
  lines.push('')

  lines.push('## Fees')
  lines.push('')
  lines.push(
    'We do not publish fees. Scope, timing and fees are set out in a written proposal after a discovery call. Public sector work is quoted on a custom basis. Cavalla Solution is not a financial adviser.',
  )
  lines.push('')

  lines.push('## Free tools')
  lines.push('')
  lines.push(
    `AI readiness assessment: twelve questions across data, skills, use cases, governance and budget, returning a score out of one hundred, a band description and three recommended next steps. Free. ${SITE.url}/assessment`,
  )
  lines.push('')

  lines.push('## Questions and answers')
  lines.push('')
  ;[...GENERAL_FAQS, ...GOV_FAQS].forEach((faq) => {
    lines.push(`Q: ${faq.question}`)
    lines.push(`A: ${faq.answer}`)
    lines.push('')
  })

  lines.push('## Webinars, the 2027 programme')
  lines.push('')
  lines.push(
    `A monthly webinar for government officials, free to attend. Every session is the third Thursday of the month at ${SESSION_TIME.full} and runs for ${SESSION_TIME.durationMins} minutes. Registrants receive the recording and a one page summary whether or not they attend. Hub: ${SITE.url}/webinars`,
  )
  lines.push('')
  WEBINARS.forEach((webinar) => {
    lines.push(
      `- ${webinar.date}, ${webinar.title}. Focus: ${webinar.focus}. For: ${webinar.audience}. ${SITE.url}/webinars/${webinar.slug}`,
    )
  })
  lines.push('')

  lines.push('## Insights')
  lines.push('')
  ARTICLES.forEach((article) => {
    lines.push(`- ${article.title} (${article.publishedAt}): ${article.summaryBlock}`)
    lines.push(`  ${SITE.url}/insights/${article.slug}`)
  })
  lines.push('')

  lines.push('## Resources')
  lines.push('')
  RESOURCES.forEach((resource) => {
    lines.push(`- ${resource.title}: ${resource.summary} ${SITE.url}/resources/${resource.slug}`)
  })
  lines.push('')

  lines.push('## Contact')
  lines.push('')
  lines.push(`Discovery calls and enquiries: ${SITE.url}/contact. A discovery call lasts thirty minutes.`)
  lines.push(`Email: ${SITE.email}. LinkedIn: ${SITE.linkedin}`)
  lines.push('')

  return lines.join('\n')
}
